<template>
  <div class="w-full max-w-md mx-auto px-4 py-6">
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-4">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3">
        <div class="flex items-center justify-between">
          <h1 class="font-semibold text-white text-xl">
            {{ isEditing ? $t('Mobile.MobileContractEventCreate.editTitle') : $t('Mobile.MobileContractEventCreate.title') }}
          </h1>

          <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
          </div>
        </div>
      </div>

      <div class="flex p-4 space-y-4">
        <AppSpinner
          v-if="fetchAction.is(States.LOADING) && isEditing"
          class="p-4 mx-auto"
        />

        <form
          v-else
          class="w-full space-y-4"
          @submit.prevent="saveContractEvent"
        >
          <div
            v-if="contractEvent"
            class="flex items-center justify-between"
          >
            <div>
              <span class="font-semibold text-gray-900">{{ contractEvent.code }}</span>
            </div>

            <div
              :class="['text-xs px-2 py-0.5 rounded-full w-fit', ContractEventStatusBadgeClasses[enumKeyFromValue(ContractEventStatus, contractEvent.status)] ?? 'bg-gray-200 text-gray-700']"
            >
              {{ ContractEventStatusTitles[enumKeyFromValue(ContractEventStatus, contractEvent.status)] }}
            </div>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileContractEventCreate.name') }} <span class="text-red-500">*</span>
            </label>

            <AppInput
              v-model="contractEventForm.form.name"
              class="form-input"
              type="text"
              name="name"
              :placeholder="$t('Mobile.MobileContractEventCreate.namePlaceholder')"
              :error="contractEventForm.getErrors('name')"
            />
          </div>

          <div class="block text-sm font-medium text-gray-700">
            Raised by: {{ contractEvent?.user?.name }}
          </div>


          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileContractEventCreate.cause') }} <span class="text-red-500">*</span>
            </label>

            <AppTextArea
              v-model="contractEventForm.form.cause"
              name="cause"
              rows="4"
              class="form-input resize-none"
              :placeholder="$t('Mobile.MobileContractEventCreate.causePlaceholder')"
              :error="contractEventForm.getErrors('cause')"
            />
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileContractEventCreate.effect') }} <span class="text-red-500">*</span>
            </label>

            <AppTextArea
              v-model="contractEventForm.form.effect"
              name="effect"
              rows="4"
              class="form-input resize-none"
              :placeholder="$t('Mobile.MobileContractEventCreate.effectPlaceholder')"
              :error="contractEventForm.getErrors('effect')"
            />
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileContractEventCreate.impact') }} <span class="text-red-500">*</span>
            </label>

            <select
              v-model.number="contractEventForm.form.impact"
              name="impact"
              class="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-ccm-blue-500 focus:ring-ccm-blue-500"
              :class="{ 'border-red-500': contractEventForm.getErrors('impact') }"
            >
              <option :value="ContractEventImpact.low">
                {{ $t('Mobile.MobileContractEventCreate.impactLow') }}
              </option>

              <option :value="ContractEventImpact.medium">
                {{ $t('Mobile.MobileContractEventCreate.impactMedium') }}
              </option>

              <option :value="ContractEventImpact.high">
                {{ $t('Mobile.MobileContractEventCreate.impactHigh') }}
              </option>
            </select>

            <p
              v-if="contractEventForm.getErrors('impact')"
              class="text-sm text-red-600"
            >
              {{ contractEventForm.getErrors('impact') }}
            </p>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileContractEventCreate.dateOfOccurrence') }}
            </label>

            <AppInput
              v-model="contractEventForm.form.date_of_occurrence"
              class="form-input"
              type="date"
              name="date_of_occurrence"
              :error="contractEventForm.getErrors('date_of_occurrence')"
            />
          </div>

          <div class="flex flex-col space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileContractEventCreate.attachments') }}
            </label>

            <template v-if="isEditing">
              <ContractEventMediaDisplay
                v-if="contractEventMediaCollection.mediaRequestAction.is(States.COMPLETE)"
                :media="contractEventMediaCollection.mediaCollection.value"
                :allow-delete="contractEvent?.status === ContractEventStatus.pending && userCanDeleteMedia"
                @delete="contractEventMediaCollection.removeMedia($event)"
              />

              <AppSpinner
                v-else
                class="p-4 mx-auto"
              />
            </template>

            <FileUpload
              v-model="contractEventForm.form.images"
              multiple
              @change="addFile"
            />

            <p
              v-if="filesUploaded.length"
              class="block text-sm font-medium text-gray-700"
            >
              New Attachments
            </p>

            <ContractEventAttachments
              v-if="filesUploaded.length"
              :length="filesUploaded.length"
            >
              <SwiperSlide
                v-for="(file, index) in filesUploaded"
                :key="index"
                class="!h-auto"
              >
                <div class="flex items-center w-full justify-center h-full">
                  <UploadedFileDisplay
                    :file="file"
                    :error="contractEventForm.getErrors(`images.${index}`)"
                    @delete="removeFile(index)"
                  />
                </div>
              </SwiperSlide>
            </ContractEventAttachments>
          </div>

          <div
            v-if="isEditing"
            class="space-y-4"
          >
            <h2 class="block text-sm font-medium text-gray-700">
              {{ $t('Mobile.MobileContractEventCreate.comments') }}
            </h2>

            <div class="space-y-2">
              <AppTextArea
                v-model="commentForm.form.comment"
                name="comment"
                rows="3"
                class="form-input resize-none"
                :error="commentForm.getErrors('comment')"
              />
            </div>

            <button
              type="button"
              class="btn btn--primary-blue w-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="commentAction.is(States.LOADING) || commentForm.form.comment.trim() === ''"
              @click="saveComment"
            >
              <span v-if="commentAction.is(States.LOADING)">{{ $t('Mobile.MobileContractEventCreate.savingComment') }}</span>

              <span v-else>{{ $t('Mobile.MobileContractEventCreate.saveComment') }}</span>
            </button>

            <div
              v-if="contractEvent?.comments?.length"
              class="space-y-4 max-h-60 overflow-y-auto"
            >
              <div
                v-for="comment in contractEvent.comments"
                :key="comment.id"
              >
                <p class="font-semibold text-sm">
                  {{ comment.creator?.name }}

                  <span class="text-sm font-normal text-gray-500">
                    {{ comment.createdAt.toLocaleString(undefined, {
                      hour12: true,
                      hour: 'numeric',
                      minute: 'numeric',
                      day: 'numeric',
                      month: 'numeric',
                      year: 'numeric'
                    }) }}
                  </span>
                </p>

                <p class="mt-0.5 text-sm whitespace-pre-wrap break-words">
                  {{ comment.body }}
                </p>
              </div>
            </div>
          </div>

          <div class="flex space-x-3 pt-2">
            <RouterLink
              :to="{ name: 'MobileContractEventsIndex' }"
              class="btn btn--secondary-blue flex-1 flex items-center justify-center"
            >
              {{ $t('Mobile.MobileContractEventCreate.cancel') }}
            </RouterLink>

            <button
              type="submit"
              class="btn btn--primary-blue flex-1 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="saveAction.is(States.LOADING)"
            >
              <span v-if="saveAction.is(States.LOADING)">{{ $t('Mobile.MobileContractEventCreate.saving') }}</span>

              <span v-else>{{ isEditing ? $t('Mobile.MobileContractEventCreate.update') : $t('Mobile.MobileContractEventCreate.submit') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import ContractEventAttachments from '@/components/Mobile/MobileContractEvent/ContractEventAttachments.vue';
import ContractEventMediaDisplay from '@/components/Mobile/MobileContractEvent/ContractEventMediaDisplay.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { useMediaCollection } from '@/composables/useMediaCollection';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateAsYMD, dateForDateInput, enumKeyFromValue } from '@/helpers';
import {
  ContractEventImpact,
  ContractEventStatus,
  ContractEventStatusBadgeClasses,
  ContractEventStatusTitles,
} from '@/models/ContractEvent';
import { useContractEventsStore } from '@/store/contractEvents';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import Swal from 'sweetalert2';
import { SwiperSlide } from 'swiper/vue';
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  contractEventId?: string;
}>();

const router = useRouter();
const contractEventsStore = useContractEventsStore();
const projectsStore = useProjectsStore();
const isEditing = computed(() => Boolean(props.contractEventId));
const fetchAction = useStoreApiAction(contractEventsStore.fetchContractEvent);
const createAction = useStoreApiAction(contractEventsStore.createContractEvent);
const updateAction = useStoreApiAction(contractEventsStore.updateContractEvent);
const storeMediaAction = useStoreApiAction(contractEventsStore.storeMedia);
const saveAction = computed(() => isEditing.value ? updateAction : createAction);

const contractEventForm = useFormData({
  name: '',
  cause: '',
  effect: '',
  impact: 0,
  date_of_occurrence: dateAsYMD(new Date()),
  images: [],
});

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (_file, index) => {
  contractEventForm.form.images.splice(index, 1);
});

const contractEventMediaCollection = useMediaCollection(contractEventsStore.media);

const contractEvent = computed(() => {
  if(!props.contractEventId) {
    return undefined;
  }

  return contractEventsStore.models
    .with('user')
    .with('comments', (query) => {
      query.with('creator')
        .orderBy('created_at', 'desc');
    }).find(parseInt(props.contractEventId));
});

const commentForm = useFormData({
  comment: '',
});

const commentAction = useStoreApiAction(contractEventsStore.comment);

watch(() => props.contractEventId, (newValue, oldValue) => {
  if(newValue !== oldValue) {
    fetchAction.request(newValue);

    contractEventMediaCollection.clear();
    contractEventMediaCollection.fetchMedia(newValue);
  }
}, { immediate: true });

watch(contractEvent, (newValue) => {
  if(newValue) {
    contractEventForm.setData(newValue, ['date_of_occurrence']);

    contractEventForm.form.date_of_occurrence = dateForDateInput(newValue.date_of_occurrence);
  }
}, { immediate: true });

const saveComment = () => {
  commentForm.resetErrors();

  if(!contractEvent.value) {
    return;
  }

  commentAction.request(contractEvent.value.id, commentForm.form)
    .then(() => {
      commentForm.resetData();
    }).catch((error) => {
      commentForm.setErrors(error.data);
    });
};

const saveContractEvent = () => {
  if(isEditing.value && props.contractEventId) {
    const payload = {
      name: contractEventForm.form.name,
      cause: contractEventForm.form.cause,
      effect: contractEventForm.form.effect,
      impact: contractEventForm.form.impact,
      date_of_occurrence: contractEventForm.form.date_of_occurrence || undefined,
    };

    updateAction.request(parseInt(props.contractEventId), payload).then(async () => {
      if(contractEventForm.form.images.length > 0) {
        const formData = new FormData();

        contractEventForm.form.images.forEach((file: File) => {
          formData.append('images[]', file);
        });

        await storeMediaAction.request(parseInt(props.contractEventId!), formData);
      }

      router.push({ name: 'MobileContractEventsIndex' });
    }).catch((error) => {
      contractEventForm.setErrors(error.data);
    });
  } else {
    createAction.request(props.projectId, contractEventForm.toFormDataObject()).then(() => {
      router.push({ name: 'MobileContractEventsIndex' });
    }).catch((error) => {
      contractEventForm.setErrors(error.data);
    });
  }
};

const userStore = useUserStore();

const userIsEngineerOrSiteManager = computed(() => {
  return userStore.isRole(['engineer', 'site_manager'], false, contractEvent.value?.project_id);
});

const userCanDeleteMedia = computed(() => {
  return (userIsEngineerOrSiteManager.value && contractEvent.value?.user_id === userStore.user?.id) ||
    userStore.isRole(
      ['project_admin', 'contract_admin', 'company_admin', 'contract_manager'],
      true,
      contractEvent.value?.project_id,
    );
});
</script>
